import { Ref, useState } from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { RichTextEditor, RichTextEditorHandle } from '@factorialco/f0-react/dist/experimental'

import TextEditor, { type EditorHandle } from 'modules/core/components/TextEditor'
import useRichTextEditor from 'modules/core/hooks/useRichTextEditor'
import i18n from 'modules/core/lib/i18n'

import { AtsMessageVariablesSelector } from 'modules/ats/components/AtsMessageVariablesSelector'
import { MessageTemplatesSelector } from 'modules/ats/components/message_templates/MessageTemplatesSelector'
import { useMessageTemplatesFeatureAvailability } from 'modules/ats/hooks/features/useMessageTemplatesFeatureAvailability'

interface Props {
  value: string
  onChange: (value?: string | null) => void
  onFiles?: (attachments?: File[] | null) => void
  initialFileState?: File[]
  placeholder: string
  showLabel?: boolean
  editorRef?: Ref<EditorHandle>
  richTextEditorRef: Ref<RichTextEditorHandle>
}

export const TextEditorWithMessageTemplates = ({
  value,
  onChange,
  onFiles,
  initialFileState = [],
  placeholder,
  showLabel,
  editorRef,
  richTextEditorRef,
}: Props) => {
  const hasMessageTemplatesFeature = useMessageTemplatesFeatureAvailability()
  const [messageTemplateId, setMessageTemplateId] = useState<string | undefined>(undefined)
  const { editorConfig, canUseRichTextEditor, loading: richTextEditorLoading } = useRichTextEditor()

  const modifyRichTextEditorContent = (content: string) => {
    if (richTextEditorRef && 'current' in richTextEditorRef) {
      richTextEditorRef.current?.setContent(content)
    }
  }

  const onMessageTemplateChanged = (templateContent: string, templateId: string) => {
    onChange(templateContent)
    setMessageTemplateId(templateId)
    modifyRichTextEditorContent(templateContent)
  }

  const onAddVariable = (variable: string) => {
    onChange(`${value} ${variable}`)
    modifyRichTextEditorContent(`${value} ${variable}`)
  }

  return (
    <>
      {showLabel && (
        <Box>
          <Text.Normal size="200" weight="medium" color="grey1000">
            {i18n.t('ats.offer_letter.modal.message_template')}
          </Text.Normal>
        </Box>
      )}

      {hasMessageTemplatesFeature && (
        <Box flexDirection="row" justifyContent="spaceBetween" alignItems="flexEnd" gap="s24">
          <MessageTemplatesSelector
            messageTemplateId={messageTemplateId}
            onMessageTemplateChanged={onMessageTemplateChanged}
          />
          <Box minWidth="fit-content">
            <AtsMessageVariablesSelector onSelect={onAddVariable} showJobData showSenderData />
          </Box>
        </Box>
      )}

      {richTextEditorLoading ? (
        <RichTextEditor.Skeleton />
      ) : canUseRichTextEditor ? (
        <RichTextEditor
          ref={richTextEditorRef}
          {...editorConfig}
          onChange={(data) => onChange(data?.value)}
          placeholder={placeholder}
          initialEditorState={{ content: value, files: initialFileState }}
          filesConfig={
            onFiles && {
              onFiles,
              multipleFiles: true,
            }
          }
          title={i18n.t('ats.offer_letter.modal.message_template')}
        />
      ) : (
        <TextEditor
          ref={editorRef}
          onChange={(newValue) => {
            if (newValue !== value) {
              setMessageTemplateId(undefined)
            }

            // ? Do not change the assertion as we could have null values or MentionChangeResult types
            // eslint-disable-next-line no-type-assertion/no-type-assertion
            onChange(newValue as string)
          }}
          placeholder={placeholder}
          onFiles={onFiles}
          initialEditorState={{ data: value, files: initialFileState }}
          height="s200"
        />
      )}
    </>
  )
}
